import countriesGeo from "@/data/countries.geo.json";
import {
  COUNTRY_ADMIN_ALIAS,
  COUNTRY_CENTROIDS,
  COUNTRY_ISO_OVERRIDE,
  isoToFlag,
} from "@/data/cases";

type GeoFeature = {
  type: "Feature";
  properties: { ADMIN: string | null; ISO_A2: string | null };
  geometry: any;
};

const features = (countriesGeo as any).features as GeoFeature[];

// ADMIN (lowercased) -> feature
const byAdminLower = new Map<string, GeoFeature>();
for (const f of features) {
  const a = f.properties.ADMIN;
  if (a) byAdminLower.set(a.toLowerCase(), f);
}

function resolveAdminName(country: string): string {
  return COUNTRY_ADMIN_ALIAS[country] ?? country;
}

export function findFeature(country: string): GeoFeature | null {
  const admin = resolveAdminName(country).toLowerCase();
  return byAdminLower.get(admin) ?? null;
}

export function getISO(country: string): string {
  if (COUNTRY_ISO_OVERRIDE[country]) return COUNTRY_ISO_OVERRIDE[country];
  const f = findFeature(country);
  return f?.properties.ISO_A2 || "";
}

export function getFlag(country: string): string {
  const iso = getISO(country);
  if (iso) return isoToFlag(iso);
  if (country === "At sea") return "🚢";
  return "🏳";
}

// Compute polygon bounds center as a fallback centroid.
function centerOfGeometry(geom: any): [number, number] | null {
  if (!geom) return null;
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  const visit = (coords: any) => {
    if (typeof coords[0] === "number") {
      const [lng, lat] = coords;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      return;
    }
    for (const c of coords) visit(c);
  };
  visit(geom.coordinates);
  if (!isFinite(minLat)) return null;
  return [(minLat + maxLat) / 2, (minLng + maxLng) / 2];
}

const centroidCache = new Map<string, [number, number]>();

export function getCentroid(country: string): [number, number] | null {
  if (COUNTRY_CENTROIDS[country]) return COUNTRY_CENTROIDS[country];
  if (centroidCache.has(country)) return centroidCache.get(country)!;
  const f = findFeature(country);
  if (!f) return null;
  const c = centerOfGeometry(f.geometry);
  if (c) centroidCache.set(country, c);
  return c;
}

// Reverse map: ADMIN name (as in geojson) -> our DB country key.
const adminToDbKey = new Map<string, string>();
for (const [dbKey, adminName] of Object.entries(COUNTRY_ADMIN_ALIAS)) {
  adminToDbKey.set(adminName, dbKey);
}

export function adminNameToCountryKey(adminName: string): string {
  return adminToDbKey.get(adminName) ?? adminName;
}

// Map demonym -> DB country key. Used to highlight countries whose
// citizens were affected even when the case itself happened elsewhere
// (e.g. a German national who died at sea).
const NATIONALITY_TO_COUNTRY: Record<string, string> = {
  german: "Germany",
  british: "United Kingdom",
  english: "United Kingdom",
  scottish: "United Kingdom",
  welsh: "United Kingdom",
  dutch: "Netherlands",
  french: "France",
  spanish: "Spain",
  swiss: "Switzerland",
  turkish: "Turkey",
  american: "United States",
  canadian: "Canada",
  argentine: "Argentina",
  argentinian: "Argentina",
  "south african": "South Africa",
  singaporean: "Singapore",
  israeli: "Israel",
  portuguese: "Portugal",
  italian: "Italy",
};

export function nationalityToCountry(nationality?: string | null): string | null {
  if (!nationality) return null;
  const key = nationality.trim().toLowerCase();
  return NATIONALITY_TO_COUNTRY[key] ?? null;
}
